import { BitkitButton, BitkitButtonProps, BitkitList, createBitkitToast } from '@bitrise/bitkit-v2';
import { Box, BoxProps } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { useMutation } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import { Fragment, ReactNode, useContext, useMemo, useState } from 'react';

import BuildListItem, { BuildListErrorItem } from '@/components/BuildListItem/BuildListItem';
import BuildListSkeletonContent from '@/components/BuildListItem/BuildListSkeletonContent';
import ConfirmAbortBuildDialog from '@/components/ConfirmAbortBuildDialog/ConfirmAbortBuildDialog';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import useStartBuild, { StartBuildErrorResponse, StartBuildResponse } from '@/hooks/useStartBuild';
import { Build, PublicApiBuild } from '@/models/Build';
import BuildStatus from '@/models/BuildStatus';
import BuildListFilters from '@/pages/App/AppDetails/types/BuildListFilters';
import AppForAppPage from '@/pages/App/types/AppForAppPage';
import BuildListEmptyContent from '@/pages/ci/BuildsSection/BuildListEmptyContent';
import BuildListLoadingErrorContent from '@/pages/ci/BuildsSection/BuildListLoadingErrorContent';
import { monolithApi } from '@/utils/api';
import { formatRelativeCustom } from '@/utils/commonFormats';
import { abortBuildPath, abortPipelinePath, rebuildBuildPath } from '@/utils/monolithApiRouteService';

const LoadingSkeletonCount = 5;
const NextPageSkeletonCount = 3;
const isSameAppWorkflowBuild = (build?: Build, otherBuild?: Build): boolean | undefined =>
  build &&
  otherBuild &&
  build.repository?.slug === otherBuild.repository?.slug &&
  build.triggeredWorkflow === otherBuild.triggeredWorkflow;

const getPreviousBuild = (builds: Build[], build: Build): Build | undefined =>
  builds?.find((b) => isSameAppWorkflowBuild(b, build) && b.status === BuildStatus.Success);

type FullWidthButtonProps = Extract<BitkitButtonProps, { asChild?: false; href?: undefined }>;
const FullWidthButton = (props: FullWidthButtonProps) => (
  <BitkitButton variant="tertiary" width="100%" marginBlock="12" {...props} />
);

const LoadMoreButton = ({
  isFetchingNextPage,
  isShowingErrorResult,
  fetchNextPage,
}: {
  isFetchingNextPage: boolean;
  isShowingErrorResult: boolean;
  fetchNextPage: () => void;
}) => {
  const isShowingFetchNextPageButton = !isFetchingNextPage && !isShowingErrorResult;

  return (
    <>
      {isFetchingNextPage && <BuildListSkeletonContent count={NextPageSkeletonCount} showAppInfo />}
      {isShowingFetchNextPageButton && (
        <FullWidthButton state={isFetchingNextPage ? 'disabled' : undefined} onClick={() => fetchNextPage()}>
          Load more
        </FullWidthButton>
      )}
    </>
  );
};

type BuildListProps = {
  app: AppForAppPage;
  builds: PublicApiBuild[] | undefined;
  count: number;
  hasNextPage?: boolean;
  fetchNextPage: () => void;
  canRunBuild: boolean;
  refetch: () => void;
  isFetchingNextPage: boolean;
  isFetchingNextPageError: boolean;
  isFetching: boolean;
  isError: boolean;
  isSuccess: boolean;
  filters: BuildListFilters;
  refetchInterval?: number | false;
} & BoxProps;

const BuildList = ({
  app,
  builds,
  count,
  hasNextPage,
  fetchNextPage,
  refetch,
  isFetchingNextPage,
  isFetchingNextPageError,
  isFetching,
  isError,
  isSuccess,
  canRunBuild,
  ...rest
}: BuildListProps) => {
  const buildsGroupedByDate = useMemo(
    () =>
      groupBy<Build>(orderBy(builds, ['triggeredAt'], ['desc']), (b: Build) =>
        // NOTE This is a potential issue, watch out for any missing build reported on the Dashboard page
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        formatRelativeCustom(new Date(b.triggeredAt!)),
      ),
    [builds],
  );

  const { user } = useContext(GlobalPropsContext);

  const [isAbortConfirmVisible, setAbortConfirmVisible] = useState(false);
  const [pipelineableToAbort, setPipelineableToAbort] = useState<Build | null>(null);

  const rebuildTriggeredBy = `manual-${user?.username}`;
  const isFetchingOnEmptyResult = isFetching && count === 0;
  const isShowingErrorResult = !isFetching && isError;
  const isShowingEmptyResult = !isFetching && count === 0;
  const isShowingErrorEmptyResult = isShowingEmptyResult && isError;
  const isShowingSuccessEmptyResult = isShowingEmptyResult && isSuccess;
  const isShowingErrorItemsResult = isShowingErrorResult && count > 0;
  const isShowingFetchingNextPageError = isShowingErrorResult && isFetchingNextPageError;

  const retryAction = () => (isFetchingNextPageError ? fetchNextPage() : refetch());

  const onRebuildSuccess = ({ data }: AxiosResponse<StartBuildResponse>) => {
    if (data.results?.length === 1 && data.results[0].buildUrl) {
      window.location.href = data.results[0].buildUrl;
    }
  };
  const onRebuildError = (error: AxiosError<StartBuildErrorResponse>) => {
    let errorTitle = 'Failed to start build';
    let errorDescription: ReactNode = 'Unknown error';
    if (error.response?.data.errorMsg) {
      errorDescription = error.response?.data.errorMsg;
    } else if (error.response?.data.results?.length) {
      const resultErrors: string[] = [];
      error.response?.data.results.forEach((result) => {
        if (result.status === 'error') {
          resultErrors.push(`${result.triggeredPipeline || result.triggeredWorkflow}: ${result.message}`);
        }
      });
      if (resultErrors.length > 1) {
        errorTitle += 's';
      }
      errorDescription = (
        <BitkitList>
          {resultErrors.map((err) => (
            <BitkitList.Item key={err}>{err}</BitkitList.Item>
          ))}
        </BitkitList>
      );
    } else {
      errorDescription = error.response?.data.message || 'Unknown error';
    }
    createBitkitToast({
      titleText: errorTitle,
      messageText: errorDescription,
      variant: 'critical',
      dismissible: true,
    });
  };

  const { mutate: rebuildWorkflow } = useMutation({
    mutationFn: ({ buildSlug, data }: any) => {
      return monolithApi.request({
        url: rebuildBuildPath(buildSlug),
        method: 'POST',
        data,
      });
    },
    onSuccess: onRebuildSuccess,
    onError: onRebuildError,
  });

  const { mutate: rebuildPipeline } = useStartBuild(
    {
      appSlug: app.slug,
      buildTriggerToken: app.buildTriggerToken,
    },
    {
      onSuccess: onRebuildSuccess,
      onError: onRebuildError,
    },
  );

  const { mutate: abortBuild } = useMutation({
    mutationFn: ({ buildSlug, data }: any) => {
      return monolithApi.request({
        url: abortBuildPath(buildSlug),
        method: 'POST',
        data,
      });
    },
    onSuccess: () => {
      setAbortConfirmVisible(false);
      setTimeout(() => {
        refetch();
      }, 1500);
    },
  });

  const { mutate: abortPipeline } = useMutation({
    mutationFn: ({ pipelineSlug, data }: any) => {
      return monolithApi.request({
        url: abortPipelinePath(app.slug, pipelineSlug),
        method: 'POST',
        data,
      });
    },
    onSuccess: () => {
      setAbortConfirmVisible(false);
      setTimeout(() => {
        refetch();
      }, 1500);
    },
  });

  const getBuildItem = (allBuilds: Build[], build: Build) => {
    return {
      build,
      previousBuild: getPreviousBuild(allBuilds, build),
      canRunBuild,
      showAppInfo: false,
      onRebuildClick: (selectedBuild: Build) => {
        if (selectedBuild.isPipeline) {
          const buildParams: any = selectedBuild.originalBuildParams || selectedBuild.triggerParams;
          buildParams.pipelineId = selectedBuild.triggeredWorkflow;
          delete buildParams.remoteAccess;
          rebuildPipeline({ buildParams, triggeredBy: rebuildTriggeredBy });
        } else {
          rebuildWorkflow({ buildSlug: selectedBuild.slug, data: { isRemote: false } });
        }
      },
      onAbortClick,
    };
  };

  const onAbortClick = (build: Build) => {
    setAbortConfirmVisible(true);
    setPipelineableToAbort(build);
  };

  const onAbortClose = () => {
    setAbortConfirmVisible(false);
    setPipelineableToAbort(null);
  };

  const onAbortConfirm = (abortReason: string) => {
    setAbortConfirmVisible(false);

    if (pipelineableToAbort?.isPipeline) {
      abortPipeline({
        pipelineSlug: pipelineableToAbort?.slug,
        data: {
          abort_reason: abortReason,
          abort_with_success: false,
          skip_notifications: true,
        },
      });
    } else {
      abortBuild({
        buildSlug: pipelineableToAbort?.slug,
        data: {
          abort_reason: abortReason,
          abort_with_success: false,
          skip_notifications: true,
        },
      });
    }
    setPipelineableToAbort(null);
  };

  return (
    <Box data-testid="build-list" paddingY={['16', '0']} {...rest}>
      <ConfirmAbortBuildDialog
        onConfirm={onAbortConfirm}
        onClose={onAbortClose}
        type="build"
        isOpen={isAbortConfirmVisible}
      />
      {isFetchingOnEmptyResult && <BuildListSkeletonContent count={LoadingSkeletonCount} showAppInfo />}
      {isShowingErrorEmptyResult && (
        <BuildListLoadingErrorContent message="Failed to load your builds!" retry={() => retryAction()} />
      )}
      {builds && (
        <div>
          {isShowingErrorItemsResult && !isFetchingNextPageError && (
            <BuildListErrorItem
              message="Failed to fetch your builds! Wait for auto-refresh or retry manually."
              retry={retryAction}
              marginY="24"
            />
          )}
          {Object.keys(buildsGroupedByDate).map((key) => (
            <Fragment key={key}>
              {buildsGroupedByDate[key].map((build, idx) => (
                <Fragment key={build.slug}>
                  {idx === 0 && (
                    <Text textStyle="heading/h6" color="text/tertiary" marginBottom="8">
                      {key}
                    </Text>
                  )}
                  <BuildListItem key={build.slug} buildItem={getBuildItem(builds, build)} app={app} marginBottom="24" />
                </Fragment>
              ))}
            </Fragment>
          ))}
          {isShowingSuccessEmptyResult && <BuildListEmptyContent />}
          {isShowingFetchingNextPageError && (
            <BuildListErrorItem message="Failed to fetch more builds!" retry={() => retryAction()} />
          )}
        </div>
      )}
      {hasNextPage && (
        <LoadMoreButton
          isFetchingNextPage={isFetchingNextPage}
          isShowingErrorResult={isShowingErrorResult}
          fetchNextPage={fetchNextPage}
        />
      )}
    </Box>
  );
};

export default BuildList;
